import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ipaddr from 'ipaddr.js';
import { MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { searchIpAccounts } from '../api/client';
import IpPrevalenceUsers from './ui/IpPrevalenceUsers';
import { TIMEZONE_OPTIONS, TZ_STORAGE_KEY, getDefaultTimezone, getTzLabel, resolveIana } from './TimelineView';
import { normalizeIsoUtc } from '../utils/timeFormat';

// IP Search (/ip-search, v1.196.0): type an IP and a date range, get every account that used
// it, across the tenants you may read (server-scoped; counts only for the rest). Backed by
// SearchIpAccounts; the result renders through the SAME ui/IpPrevalenceUsers as the Alerts
// page and the case IP tab, so the withheld and truncation disclosures live in one place.
// Open to every portal user. Every search is audited server-side.

const PRESETS = [
  { id: '7', label: '7d', days: 7 },
  { id: '30', label: '30d', days: 30 },
  { id: '90', label: '90d', days: 90 },
  { id: '365', label: '1y', days: 365 },
];
const MAX_SPAN_DAYS = 366; // mirrors Get-IpAccountSearchMaxSpanDays; the server is authoritative

const utcToday = () => new Date().toISOString().slice(0, 10);
function utcDaysAgo(n) {
  const d = new Date(`${utcToday()}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}
function spanDays(from, to) {
  const a = Date.parse(`${from}T00:00:00Z`); const b = Date.parse(`${to}T00:00:00Z`);
  return Number.isNaN(a) || Number.isNaN(b) ? NaN : Math.round((b - a) / 86400000) + 1;
}
function canonicalIp(raw) {
  let s = (raw || '').trim();
  const br = s.match(/^\[([^\]]+)\](:\d+)?$/);
  if (br) s = br[1];
  else if (/^[0-9.]+:\d+$/.test(s)) s = s.replace(/:\d+$/, '');
  if (!ipaddr.isValid(s)) return null;
  if (ipaddr.IPv4.isValid(s) && !ipaddr.IPv4.isValidFourPartDecimal(s)) return null;
  return ipaddr.parse(s).toString().toLowerCase();
}

export default function IpAccountSearchPage() {
  const [params, setParams] = useSearchParams();
  const [ipInput, setIpInput] = useState(params.get('ip') || '');
  const [from, setFrom] = useState(params.get('from') || utcDaysAgo(29));
  const [to, setTo] = useState(params.get('to') || utcToday());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const [selectedTz, setSelectedTz] = useState(getDefaultTimezone);
  useEffect(() => {
    try { localStorage.setItem(TZ_STORAGE_KEY, selectedTz); } catch { /* ignore */ }
  }, [selectedTz]);
  const tzIana = resolveIana((TIMEZONE_OPTIONS.find((t) => t.id === selectedTz) || TIMEZONE_OPTIONS[0]).iana);
  const formatTs = useCallback((ts) => {
    if (!ts) return '';
    const d = new Date(normalizeIsoUtc(ts));
    if (Number.isNaN(d.getTime())) return String(ts);
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tzIana, year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(d);
  }, [tzIana]);

  const runSearch = useCallback(async (ipRaw, f, t) => {
    setError(null);
    const ip = canonicalIp(ipRaw);
    if (!ip) { setError('Enter a valid IPv4 or IPv6 address.'); return; }
    const days = spanDays(f, t);
    if (Number.isNaN(days)) { setError('Choose a start and end date.'); return; }
    if (days < 1) { setError('The start date must be on or before the end date.'); return; }
    if (days > MAX_SPAN_DAYS) { setError(`The date range is ${days} days; the maximum is ${MAX_SPAN_DAYS}.`); return; }
    setParams({ ip, from: f, to: t }, { replace: true });
    setLoading(true);
    try {
      setData(await searchIpAccounts(ip, f, t));
    } catch (e) {
      setData(null);
      setError(e?.body?.code === 'search_timeout'
        ? 'The search took too long. Try a shorter date range.'
        : (e?.message || 'The search failed.'));
    } finally {
      setLoading(false);
    }
  }, [setParams]);

  // A link carrying ?ip= (e.g. shared by a colleague) searches on arrival.
  useEffect(() => {
    if (params.get('ip')) runSearch(params.get('ip'), from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (days) => { setFrom(utcDaysAgo(days - 1)); setTo(utcToday()); };
  const activePreset = PRESETS.find((p) => to === utcToday() && from === utcDaysAgo(p.days - 1))?.id;

  // Coverage: a window opening before a feed's first day only partly covers it. The server
  // MEASURES each feed's first day per request; an unknown start is said to be unknown.
  const coverageNotes = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (const key of ['interactive', 'nonInteractive']) {
      const f = data.feeds?.[key];
      if (!f) continue;
      if (!f.dataFrom) out.push(`Could not check how far back ${f.label.toLowerCase()} go; results may be incomplete.`);
      // State only THIS feed's limit. Saying the other feed covers the gap was false whenever
      // the window also opens before the other feed's first day (found on DEV, v1.196.1).
      else if (data.from < f.dataFrom) out.push(`${f.label} are only available from ${f.dataFrom}, so this search has none from before that date.`);
    }
    return out;
  }, [data]);

  const windowLabel = data ? `between ${data.from} and ${data.to}` : '';

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div>
        <h1 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
          <MagnifyingGlassIcon className="h-6 w-6 text-sky-400" />
          IP Search
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Find every account that signed in from an IP address, or whose sign-in token was used from it,
          in the tenants you can access. Searches interactive and non-interactive sign-ins.
        </p>
      </div>

      <form
        className="card p-4 flex flex-wrap items-end gap-3"
        onSubmit={(e) => { e.preventDefault(); runSearch(ipInput, from, to); }}
      >
        <label className="flex flex-col text-xs text-gray-400 gap-1">
          IP address
          <input
            className="input-field w-72 font-mono"
            value={ipInput}
            onChange={(e) => setIpInput(e.target.value)}
            placeholder="e.g. 203.0.113.7 or 2001:db8::1"
            autoFocus
          />
        </label>
        <label className="flex flex-col text-xs text-gray-400 gap-1">
          From (UTC day)
          <input type="date" className="input-field" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="flex flex-col text-xs text-gray-400 gap-1">
          To (UTC day)
          <input type="date" className="input-field" value={to} min={from} max={utcToday()} onChange={(e) => setTo(e.target.value)} />
        </label>
        <div className="flex gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p.days)}
              className={`px-2 py-1.5 rounded text-xs border ${activePreset === p.id ? 'border-sky-500 text-sky-300 bg-sky-950/40' : 'border-gray-700 text-gray-400 hover:text-gray-200'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button type="submit" className="btn-primary inline-flex items-center gap-1.5" disabled={loading}>
          {loading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <MagnifyingGlassIcon className="h-4 w-4" />}
          {loading ? 'Searching…' : 'Search'}
        </button>
        <label className="flex flex-col text-xs text-gray-400 gap-1 ml-auto">
          Times shown in
          <select className="input-field" value={selectedTz} onChange={(e) => setSelectedTz(e.target.value)}>
            {TIMEZONE_OPTIONS.map((tz) => <option key={tz.id} value={tz.id}>{tz.label}</option>)}
          </select>
        </label>
      </form>

      {error && <div className="text-sm text-red-400">{error}</div>}

      {data && !error && (
        <div className={`space-y-2 ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="text-xs text-gray-500">
            {data.ip} · {data.from} to {data.to} (UTC days, {data.windowDays} day{data.windowDays === 1 ? '' : 's'})
            {data.elapsedMs != null && ` · ${(data.elapsedMs / 1000).toFixed(1)}s`}
            {' · times in '}{getTzLabel(selectedTz)}
          </div>
          {coverageNotes.map((t) => (
            <div key={t} className="text-xs text-amber-300/90">{t}</div>
          ))}
          <div className="text-xs">
            <IpPrevalenceUsers data={data} ip={data.ip} formatTs={formatTs} standalone windowLabel={windowLabel} />
          </div>
          <p className="text-[11px] text-gray-600">
            Interactive sign-ins are the user signing in. Non-interactive sign-ins are an app using an existing
            sign-in token, which is where a stolen token used from another address appears. An address shared by
            many people (an office, a VPN, a cloud service) can list many legitimate accounts.
          </p>
        </div>
      )}
    </div>
  );
}
